import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { MapPin, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { isTech } from "../lib/roles";
import { statusHex, JOB_STATUSES } from "../lib/jobStatus";
import { fmtKc } from "../lib/format";

export default function MapPage() {
    const { user } = useAuth();
    const tech = isTech(user);
    const mapRef = useRef(null);
    const containerRef = useRef(null);
    const [jobs, setJobs] = useState([]);
    const [geocoding, setGeocoding] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    useEffect(() => {
        api.get("/jobs").then((r) => setJobs(r.data));
    }, []);

    // Init map once Leaflet loaded
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;
        const tryInit = () => {
            if (!window.L) { setTimeout(tryInit, 200); return; }
            const map = window.L.map(containerRef.current).setView([49.75, 15.5], 7);
            window.L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
                maxZoom: 19,
                attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }).addTo(map);
            mapRef.current = map;
        };
        tryInit();
    }, []);

    // Draw markers whenever jobs change
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !window.L) return;
        // Remove existing markers
        map.eachLayer((l) => { if (l._icon || l._url === undefined) { /* keep tile layer */ } });
        // Simpler: track markers on the map object
        if (map._customMarkers) {
            map._customMarkers.forEach((m) => map.removeLayer(m));
        }
        map._customMarkers = [];

        const withCoords = jobs.filter((j) => j.lat && j.lng);
        const bounds = [];
        withCoords.forEach((j) => {
            const color = statusHex(j.status);
            const icon = window.L.divIcon({
                className: "",
                html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>`,
                iconSize: [14, 14], iconAnchor: [7, 7],
            });
            const m = window.L.marker([j.lat, j.lng], { icon }).addTo(map);
            const total = (j.costs || []).reduce((s, c) => s + (c.total || c.quantity * c.unit_price || 0), 0);
            m.bindPopup(`
                <div style="font-family:sans-serif;min-width:180px">
                    <div style="font-weight:700;color:#111;margin-bottom:4px">${escape(j.title)}</div>
                    <div style="font-size:12px;color:#666">${escape(j.client_name || "—")}</div>
                    <div style="font-size:11px;color:#888;margin-top:4px">${escape(j.site_address || "")}</div>
                    ${tech ? "" : `<div style="font-size:11px;color:#666;margin-top:6px">Náklady: <b>${fmtKc(total)}</b></div>`}
                    <a href="/zakazky/${j.id}" style="display:inline-block;margin-top:8px;padding:4px 8px;background:#A67E13;color:white;text-decoration:none;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em">Otevřít →</a>
                </div>
            `);
            map._customMarkers.push(m);
            bounds.push([j.lat, j.lng]);
        });
        if (bounds.length > 0) {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
        }
    }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps

    const geocodeMissing = async () => {
        const missing = jobs.filter((j) => (!j.lat || !j.lng) && (j.site_address || "").trim());
        if (missing.length === 0) { toast.info("Všechny zakázky už mají souřadnice."); return; }
        setGeocoding(true);
        setProgress({ done: 0, total: missing.length });
        let done = 0;
        for (const j of missing) {
            try {
                const { data } = await api.post(`/jobs/${j.id}/geocode`);
                setJobs((prev) => prev.map((x) => x.id === j.id ? { ...x, lat: data.lat, lng: data.lng } : x));
            } catch (e) { /* skip */ }
            done += 1;
            setProgress({ done, total: missing.length });
            await new Promise((r) => setTimeout(r, 1100)); // Nominatim rate limit
        }
        setGeocoding(false);
        toast.success(`Hotovo — ${done} adres nalezeno`);
    };

    const withCoords = jobs.filter((j) => j.lat && j.lng).length;
    const noAddr = jobs.filter((j) => !(j.site_address || "").trim()).length;

    return (
        <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto animate-fade-in" data-testid="map-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow mb-2">Přehled</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Mapa staveb</h1>
                    <p className="text-sm text-slate-600 mt-2">
                        Zobrazeno {withCoords} z {jobs.length} zakázek.
                        {noAddr > 0 && ` (${noAddr} bez adresy)`}
                    </p>
                </div>
                {!tech && <button onClick={geocodeMissing} disabled={geocoding} data-testid="geocode-btn"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
                    {geocoding ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {progress.done}/{progress.total}</>
                    ) : (
                        <><MapPin className="w-4 h-4" /> Načíst souřadnice</>
                    )}
                </button>}
            </div>

            <div className="flex items-center gap-4 mb-3 text-xs text-slate-600 flex-wrap">
                {JOB_STATUSES.map((s) => <Legend key={s.key} color={s.hex} label={s.label} />)}
            </div>

            <div ref={containerRef} className="w-full h-[65vh] border border-slate-200 bg-slate-100" data-testid="map-container"></div>
        </div>
    );
}

function Legend({ color, label }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full border-2 border-white shadow" style={{ background: color }}></span>
            <span>{label}</span>
        </div>
    );
}

function escape(s) {
    return String(s || "").replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "\"": "&quot;", "'": "&#39;" }[c]));
}
